package services.http

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import it.neckar.ktor.client.featureFlagsHeader
import it.neckar.ktor.client.get
import it.neckar.ktor.client.parameter
import it.neckar.ktor.client.postJson
import it.neckar.ktor.client.putJson
import it.neckar.ktor.client.safeTryFetch
import it.neckar.lizergy.model.assemblyPortfolio.ResolvedAssemblyPortfolio
import it.neckar.lizergy.model.configuration.PhotovoltaicsConfiguration.PhotovoltaicsConfigurationId
import it.neckar.lizergy.model.configuration.PlannerConfiguration
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.configuration.quote.QuoteSnapshot
import it.neckar.lizergy.model.http.EndpointsParameters
import it.neckar.lizergy.model.price.ManualQuoteElements
import it.neckar.lizergy.model.price.PriceListExportCategory
import it.neckar.lizergy.model.price.ResolvedEarningsDistribution
import it.neckar.lizergy.model.project.ArchiveReasons
import it.neckar.lizergy.model.project.Blueprint
import it.neckar.lizergy.model.project.ProjectConfiguration.PhotovoltaicsProjectId
import it.neckar.lizergy.model.project.ResolvedBlueprint
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.lizergy.model.project.Verification
import it.neckar.lizergy.model.project.process.state.LizergyProcessStateEntry
import it.neckar.lizergy.model.state.SendProcessStatesTuple
import it.neckar.lizergy.model.state.processStatesClient.DuplicateProcessStatesRequest
import it.neckar.lizergy.model.state.processStatesClient.FetchProcessStatesForComponentResponse
import it.neckar.lizergy.model.state.processStatesClient.FetchProcessStatesResponse
import it.neckar.lizergy.model.state.processStatesClient.ReassignProcessStatesRequest
import it.neckar.lizergy.model.state.processStatesClient.SendProcessStateResponse
import it.neckar.lizergy.model.state.processStatesClient.SendProcessStatesResponse
import it.neckar.lizergy.model.stumps.AssemblyBasement
import it.neckar.lizergy.model.stumps.AssemblyRoof
import it.neckar.lizergy.model.stumps.Documentation
import it.neckar.lizergy.model.stumps.FinishingUp
import it.neckar.lizergy.model.stumps.GridAssessment
import it.neckar.lizergy.model.stumps.StartupOperations
import it.neckar.rest.NoContentResponse
import it.neckar.user.UserLoginName
import serialized.ModuleLayoutsConfiguration
import serialized.SerializedBlueprint
import serialized.SerializedEarningsDistribution
import serialized.SerializedPhotovoltaicsConfiguration
import serialized.SerializedProject
import serialized.unResolve
import services.storage.http.ArchiveProjectResponse
import services.storage.http.FetchAdvanceInvoiceResponse
import services.storage.http.FetchAssemblyBasementResponse
import services.storage.http.FetchAssemblyPortfolioResponse
import services.storage.http.FetchAssemblyRoofResponse
import services.storage.http.FetchAvailableAccountingProjectsResponse
import services.storage.http.FetchAvailableProjectsResponse
import services.storage.http.FetchBlueprintResponse
import services.storage.http.FetchConfigurationResponse
import services.storage.http.FetchConfigurationsResponse
import services.storage.http.FetchDocumentationResponse
import services.storage.http.FetchFinalAccountResponse
import services.storage.http.FetchFinishingUpResponse
import services.storage.http.FetchGridAssessmentResponse
import services.storage.http.FetchModuleLayoutsResponse
import services.storage.http.FetchOrderSpecialMaterialResponse
import services.storage.http.FetchProjectResponse
import services.storage.http.FetchQuoteSnapshotResponse
import services.storage.http.FetchQuoteSnapshotsResponse
import services.storage.http.FetchSearchForProjectsResponse
import services.storage.http.FetchStartupOperationsResponse
import services.storage.http.FetchStaticPriceListResponse
import services.storage.http.FetchSwitchMeterBoxResponse
import services.storage.http.SaveQuoteConfigurationRequest
import services.storage.http.SendResolvedProjectRequest
import kotlin.uuid.Uuid

/**
 * Stores / retrieves information using REST
 */
class ProjectQueryClientService(val httpClient: HttpClient, val urlSupport: PlannerUrlSupport) {

  /**
   * Loads the [StaticPriceList]
   */
  suspend fun fetchStaticPriceList(additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): FetchStaticPriceListResponse {
    return safeTryFetch(FetchStaticPriceListResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.staticPriceList()) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchStaticPriceListResponse.serializer())
    }
  }

  /**
   * Downloads one product category of the price list as a spreadsheet
   */
  suspend fun downloadPriceListXlsx(category: PriceListExportCategory): ByteArray {
    return httpClient
      .get(urlSupport.projectQuery.priceListExport(category)) {
        featureFlagsHeader()
        accept(ContentType.Application.Xlsx)
      }.readRawBytes()
  }

  /**
   * Downloads the accounting-relevant project previews as a spreadsheet
   */
  suspend fun downloadAccountingProjectPreviewsXlsx(): ByteArray {
    return httpClient
      .get(urlSupport.projectQuery.accountingProjectPreviewsExport()) {
        featureFlagsHeader()
        accept(ContentType.Application.Xlsx)
      }.readRawBytes()
  }

  /**
   * Downloads all visible project previews with their process states as a spreadsheet
   */
  suspend fun downloadProjectPreviewsXlsx(): ByteArray {
    return httpClient
      .get(urlSupport.projectQuery.projectPreviewsExport()) {
        featureFlagsHeader()
        accept(ContentType.Application.Xlsx)
      }.readRawBytes()
  }

  /**
   * Returns the available [ProjectPreviews] for the given company.
   */
  suspend fun fetchAvailableProjects(additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): FetchAvailableProjectsResponse {
    return safeTryFetch(FetchAvailableProjectsResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.projects()) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchAvailableProjectsResponse.serializer())
    }
  }

  suspend fun fetchAvailableAccountingProjects(additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): FetchAvailableAccountingProjectsResponse {
    return safeTryFetch(FetchAvailableAccountingProjectsResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.accountingProjects()) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchAvailableAccountingProjectsResponse.serializer())
    }
  }

  suspend fun fetchProject(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchProjectResponse {
    return safeTryFetch(FetchProjectResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.project(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchProjectResponse.serializer())
    }
  }

  /**
   * Loads the [ResolvedProjectPreviews]
   */
  suspend fun queryProjectPreviews(
    request: ProjectQueryRequest,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): ProjectQueryResponse {
    return safeTryFetch(ProjectQueryResponse.Failed) {
      httpClient.postJson(urlSupport.projectQuery.projectPreviewsUrl, request) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(ProjectQueryResponse.serializer())
    }
  }

  suspend fun countProjectPreviews(
    requestedProjectCountsForPhases: List<ProjectCountRequestForPhase>,
    loggedInUser: UserLoginName,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): ProjectCountQueryResponse {
    val request = ProjectCountQueryRequest(
      requestedProjectCountsForPhases = requestedProjectCountsForPhases,
      loggedInUser = loggedInUser,
    )
    return safeTryFetch(ProjectCountQueryResponse.Failed) {
      httpClient.postJson(urlSupport.projectQuery.projectCountsUrl, request) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(ProjectCountQueryResponse.serializer())
    }
  }

  suspend fun accountingProjectPreviews(request: AccountingProjectQueryRequest, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): AccountingProjectQueryResponse {
    return safeTryFetch(AccountingProjectQueryResponse.Failed) {
      httpClient.postJson(urlSupport.projectQuery.accountingProjectPreviewsUrl, request) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(AccountingProjectQueryResponse.serializer())
    }
  }

  suspend fun accountingQueryProjectPreviewsWithRevenues(request: AccountingProjectQueryRequest, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): AccountingProjectQueryResponse {
    return safeTryFetch(AccountingProjectQueryResponse.Failed) {
      httpClient.postJson(urlSupport.projectQuery.accountingProjectPreviewsWithRevenuesUrl, request) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(AccountingProjectQueryResponse.serializer())
    }
  }

  suspend fun allAccountingProjectPreviewsAsCSV(
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): AllAccountingProjectPreviewsAsCSVResponse {
    return safeTryFetch(AllAccountingProjectPreviewsAsCSVResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.allAccountingProjectPreviewsAsCSVUrl) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(AllAccountingProjectPreviewsAsCSVResponse.serializer())
    }
  }

  /**
   * Finds projects by the given search string
   */
  suspend fun searchProjects(
    searchTerm: String,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchSearchForProjectsResponse {
    return safeTryFetch(FetchSearchForProjectsResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.projectsSearch()) {
        featureFlagsHeader()
        parameter(EndpointsParameters.searchTerm, searchTerm)
        additionalRequestConfiguration()
      }.plannerBody(FetchSearchForProjectsResponse.serializer())
    }
  }


  suspend fun fetchBlueprint(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchBlueprintResponse {
    return safeTryFetch(FetchBlueprintResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.blueprint(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchBlueprintResponse.serializer())
    }
  }

  suspend fun fetchOrderSpecialMaterial(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchOrderSpecialMaterialResponse {
    return safeTryFetch(FetchOrderSpecialMaterialResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.orderSpecialMaterial(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchOrderSpecialMaterialResponse.serializer())
    }
  }

  suspend fun fetchGridAssessment(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchGridAssessmentResponse {
    return safeTryFetch(FetchGridAssessmentResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.gridAssessment(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchGridAssessmentResponse.serializer())
    }
  }

  suspend fun fetchAssemblyPortfolio(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchAssemblyPortfolioResponse {
    return safeTryFetch(FetchAssemblyPortfolioResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.assemblyPortfolio(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchAssemblyPortfolioResponse.serializer())
    }
  }

  suspend fun fetchAdvanceInvoice(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchAdvanceInvoiceResponse {
    return safeTryFetch(FetchAdvanceInvoiceResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.advanceInvoice(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchAdvanceInvoiceResponse.serializer())
    }
  }

  suspend fun fetchAssemblyRoof(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchAssemblyRoofResponse {
    return safeTryFetch(FetchAssemblyRoofResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.assemblyRoof(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchAssemblyRoofResponse.serializer())
    }
  }

  suspend fun fetchAssemblyBasement(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchAssemblyBasementResponse {
    return safeTryFetch(FetchAssemblyBasementResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.assemblyBasement(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchAssemblyBasementResponse.serializer())
    }
  }

  suspend fun fetchSwitchMeterBox(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchSwitchMeterBoxResponse {
    return safeTryFetch(FetchSwitchMeterBoxResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.switchMeterBox(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchSwitchMeterBoxResponse.serializer())
    }
  }

  suspend fun fetchStartupOperations(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchStartupOperationsResponse {
    return safeTryFetch(FetchStartupOperationsResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.startupOperations(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchStartupOperationsResponse.serializer())
    }
  }

  suspend fun fetchFinishingUp(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchFinishingUpResponse {
    return safeTryFetch(FetchFinishingUpResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.finishingUp(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchFinishingUpResponse.serializer())
    }
  }

  suspend fun fetchFinalAccount(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchFinalAccountResponse {
    return safeTryFetch(FetchFinalAccountResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.finalAccount(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchFinalAccountResponse.serializer())
    }
  }

  suspend fun fetchDocumentation(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchDocumentationResponse {
    return safeTryFetch(FetchDocumentationResponse.bad()) {
      httpClient.get(urlSupport.projectQuery.documentation(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchDocumentationResponse.serializer())
    }
  }

  suspend fun listConfigurations(
    projectId: PhotovoltaicsProjectId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchConfigurationsResponse {
    return safeTryFetch(FetchConfigurationsResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.configurationsForProject(projectId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchConfigurationsResponse.serializer())
    }
  }

  suspend fun findConfiguration(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchConfigurationResponse {
    return safeTryFetch(FetchConfigurationResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.configuration(projectId, configurationId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchConfigurationResponse.serializer())
    }
  }

  suspend fun listModuleLayouts(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchModuleLayoutsResponse {
    return safeTryFetch(FetchModuleLayoutsResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.moduleLayouts(projectId, configurationId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchModuleLayoutsResponse.serializer())
    }
  }

  suspend fun listQuoteSnapshots(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchQuoteSnapshotsResponse {
    return safeTryFetch(FetchQuoteSnapshotsResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.quoteSnapshots(projectId, configurationId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchQuoteSnapshotsResponse.serializer())
    }
  }

  suspend fun fetchQuoteSnapshot(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    quoteSnapshotId: QuoteSnapshot.QuoteSnapshotId,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): FetchQuoteSnapshotResponse {
    return safeTryFetch(FetchQuoteSnapshotResponse.Failed) {
      httpClient.get(urlSupport.projectQuery.quoteSnapshot(projectId, configurationId, quoteSnapshotId)) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(FetchQuoteSnapshotResponse.serializer())
    }
  }


  suspend fun sendProject(
    project: SerializedProject,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.project(project.projectId), project) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendProject(
    resolvedProject: ResolvedProject,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return sendProject(resolvedProject.unResolve(), additionalRequestConfiguration)
  }

  /**
   * Saves the given [ResolvedProject] including all its individual parts
   */
  suspend fun sendResolvedProject(
    sendResolvedProjectRequest: SendResolvedProjectRequest,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.resolvedProject(sendResolvedProjectRequest.photovoltaicsProject.projectId), sendResolvedProjectRequest) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendResolvedProject(
    resolvedProject: ResolvedProject,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return sendResolvedProject(SendResolvedProjectRequest(resolvedProject), additionalRequestConfiguration)
  }

  /**
   * Saves the given [ResolvedProject] including all its individual parts
   */
  suspend fun sendMultipleResolvedProjects(
    projectsRequests: List<SendResolvedProjectRequest>,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.multipleResolvedProjects(), projectsRequests) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun archiveProject(
    projectId: PhotovoltaicsProjectId,
    archiveReasons: ArchiveReasons,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): ArchiveProjectResponse {
    return safeTryFetch(ArchiveProjectResponse.bad()) {
      httpClient.putJson(urlSupport.projectQuery.archiveProjectUrl(projectId), archiveReasons) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.plannerBody(ArchiveProjectResponse.serializer())
    }
  }

  /**
   * Saves the [Blueprint] for the given project
   */
  suspend fun sendBlueprint(
    blueprint: SerializedBlueprint,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.blueprint(blueprint.projectId), blueprint) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendBlueprint(
    blueprint: ResolvedBlueprint,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return sendBlueprint(blueprint.unResolve(), additionalRequestConfiguration)
  }

  suspend fun sendVerification(
    projectId: PhotovoltaicsProjectId,
    verification: Verification,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.verifications(projectId), verification) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendGridAssessment(
    projectId: PhotovoltaicsProjectId,
    gridAssessment: GridAssessment,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.gridAssessment(projectId), gridAssessment) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendAssemblyPortfolio(
    projectId: PhotovoltaicsProjectId,
    assemblyPortfolio: ResolvedAssemblyPortfolio,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.assemblyPortfolio(projectId), assemblyPortfolio.unResolve()) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendAssemblyBasement(
    projectId: PhotovoltaicsProjectId,
    assemblyBasement: AssemblyBasement,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.assemblyBasement(projectId), assemblyBasement) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendAssemblyRoof(
    projectId: PhotovoltaicsProjectId,
    assemblyRoof: AssemblyRoof,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.assemblyRoof(projectId), assemblyRoof) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  suspend fun sendStartupOperations(
    projectId: PhotovoltaicsProjectId,
    startupOperations: StartupOperations,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.startupOperations(projectId), startupOperations) {
      featureFlagsHeader()
      additionalRequestConfiguration()
    }.body()
  }

  suspend fun sendFinishingUp(
    projectId: PhotovoltaicsProjectId,
    finishingUp: FinishingUp,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.finishingUp(projectId), finishingUp) {
      featureFlagsHeader()
      additionalRequestConfiguration()
    }.body()
  }

  suspend fun sendDocumentation(
    projectId: PhotovoltaicsProjectId,
    documentation: Documentation,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.documentation(projectId), documentation) {
      featureFlagsHeader()
      additionalRequestConfiguration()
    }.body()
  }

  @IgnorableReturnValue
  suspend fun sendConfiguration(
    projectId: PhotovoltaicsProjectId,
    configuration: SerializedPhotovoltaicsConfiguration,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.configuration(projectId, configuration.configurationId), configuration) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  /**
   * Saves the [PlannerConfiguration] for the given project
   */
  @IgnorableReturnValue
  suspend fun sendConfiguration(
    projectId: PhotovoltaicsProjectId,
    configuration: PlannerConfiguration,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return sendConfiguration(projectId, configuration.unResolve(), additionalRequestConfiguration)
  }

  suspend fun sendQuoteSnapshot(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    quoteSnapshot: QuoteSnapshot,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.quoteSnapshot(projectId, configurationId, quoteSnapshot.id), quoteSnapshot) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  /**
   * Saves the entire [QuoteConfiguration] for the given project
   */
  @IgnorableReturnValue
  suspend fun sendQuoteConfiguration(
    projectId: PhotovoltaicsProjectId,
    saveQuoteConfigurationRequest: SaveQuoteConfigurationRequest,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.quoteConfiguration(projectId, saveQuoteConfigurationRequest.configuration.configurationId), saveQuoteConfigurationRequest) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  @IgnorableReturnValue
  suspend fun sendQuoteConfiguration(
    projectId: PhotovoltaicsProjectId,
    quoteConfiguration: QuoteConfiguration,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return sendQuoteConfiguration(projectId, SaveQuoteConfigurationRequest(quoteConfiguration), additionalRequestConfiguration)
  }

  @IgnorableReturnValue
  suspend fun sendEarningsDistribution(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    earningsDistribution: SerializedEarningsDistribution,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.earningsDistribution(projectId, configurationId), earningsDistribution) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }

  @IgnorableReturnValue
  suspend fun sendEarningsDistribution(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    earningsDistribution: ResolvedEarningsDistribution,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return sendEarningsDistribution(projectId, configurationId, earningsDistribution.unResolve(), additionalRequestConfiguration)
  }

  @IgnorableReturnValue
  suspend fun sendManualQuoteElements(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    manualQuoteElements: ManualQuoteElements,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.manualQuoteElements(projectId, configurationId), manualQuoteElements) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }


  /**
   * Saves the [ModuleLayoutsConfiguration] for the given project
   */
  suspend fun sendModuleLayouts(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    moduleLayouts: ModuleLayoutsConfiguration,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.moduleLayouts(projectId, configurationId), moduleLayouts) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }


  @IgnorableReturnValue
  suspend fun sendQuoteSnapshots(
    projectId: PhotovoltaicsProjectId,
    configurationId: PhotovoltaicsConfigurationId,
    quoteSnapshot: QuoteSnapshot,
    additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {},
  ): NoContentResponse {
    return httpClient.putJson(urlSupport.projectQuery.quoteSnapshot(projectId, configurationId, quoteSnapshot.id), quoteSnapshot) {
        featureFlagsHeader()
        additionalRequestConfiguration()
      }.body()
  }


  suspend fun fetchProcessStatesForComponent(processStatesFor: Uuid, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): FetchProcessStatesForComponentResponse {
    return safeTryFetch(FetchProcessStatesForComponentResponse.bad()) {
      httpClient
        .get(urlSupport.projectQuery.processStates(processStatesFor)) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(FetchProcessStatesForComponentResponse.serializer())
    }
  }

  @IgnorableReturnValue
  suspend fun sendProcessState(processStateFor: Uuid, newProcessState: LizergyProcessStateEntry): SendProcessStateResponse {
    return safeTryFetch(SendProcessStateResponse.bad()) {
      httpClient
        .putJson<LizergyProcessStateEntry>(urlSupport.projectQuery.processStates(processStateFor), newProcessState) {
          featureFlagsHeader()
        }.plannerBody(SendProcessStateResponse.serializer())
    }
  }

  @IgnorableReturnValue
  suspend fun sendMultipleProcessStates(processStatesForComponents: List<SendProcessStatesTuple>): SendProcessStatesResponse {
    return safeTryFetch(SendProcessStatesResponse.bad()) {
      httpClient
        .putJson(urlSupport.projectQuery.multipleProcessStates(), processStatesForComponents) {
          featureFlagsHeader()
        }.plannerBody(SendProcessStatesResponse.serializer())
    }
  }

  @IgnorableReturnValue
  suspend fun duplicateProcessStates(mapOfOldToNewUuids: Map<Uuid, Uuid>): SendProcessStateResponse {
    return safeTryFetch(SendProcessStateResponse.bad()) {
      httpClient
        .putJson(urlSupport.projectQuery.duplicateProcessStates(), DuplicateProcessStatesRequest(mapOfOldToNewUuids)) {
          featureFlagsHeader()
        }.plannerBody(SendProcessStateResponse.serializer())
    }
  }

  @IgnorableReturnValue
  suspend fun reassignProcessStates(reassignProcessStatesRequest: ReassignProcessStatesRequest, additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): SendProcessStatesResponse {
    return safeTryFetch(SendProcessStatesResponse.bad()) {
      httpClient
        .putJson(urlSupport.projectQuery.reassignProcessStates(), reassignProcessStatesRequest) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(SendProcessStatesResponse.serializer())
    }
  }

  suspend fun fetchAllProcessStates(additionalRequestConfiguration: HttpRequestBuilder.() -> Unit = {}): FetchProcessStatesResponse {
    return safeTryFetch(FetchProcessStatesResponse.bad()) {
      httpClient
        .get(urlSupport.projectQuery.allProcessStates()) {
          featureFlagsHeader()
          additionalRequestConfiguration()
        }.plannerBody(FetchProcessStatesResponse.serializer())
    }
  }

}
